import type { ShopperStoresTypes } from 'commerce-sdk-isomorphic'
import { SFCCStore } from '~/types/Store'
import { salesforceErrorHandler } from '~/utils/errorUtils'

// SAFETY: SFCCStore only names the site's `c_*` store attributes, which the SDK returns untyped on every store.
const asSfccStores = (stores: ShopperStoresTypes.Store[]) => stores as SFCCStore[]

/**
 * A composable function that provides methods to fetch store data from salesforce.
 *
 * @returns {Object} An object containing methods to fetch stores by different criteria, and reactive properties for loading, and error states.
 *
 * @function
 * @name useStores
 *
 * @example
 * const { fetchStoresByIds, fetchStoresByLocation, loading, error } = useStores();
 */
export default function useStores() {
  const nuxtApp = useNuxtApp()
  const locale = nuxtApp.$i18n.locale.value
  const siteId = localeConfig[locale]?.siteId
  const countryCode = localeConfig[locale].country
  const mainPostcode = localeConfig[locale].mainPostcode
  const loading = ref(false)
  const error = ref()

  /**
   * Fetches stores by their IDs.
   *
   * @param {string[]} ids - An array of store IDs to fetch.
   * @returns {Promise<SFCCStore[]>} A promise that resolves to an array of SFCCStore objects.
   * @throws Will throw an error if the request fails, handled by `salesforceErrorHandler`.
   *
   * @remarks
   * This function sets the `loading` state to `true` while fetching the data and sets it back to `false` once the operation is complete.
   * If an error occurs during the fetch operation, it is caught and assigned to the `error` state, and logged to the console.
   */
  async function fetchStoresByIds(ids: string[]) {
    try {
      loading.value = true
      const response = await useShopper().shopperStores.getStores({
        headers: await useShopper().getAuthHeaders(),
        parameters: {
          ids: ids.join(','),
          siteId,
          ...sfccLocaleParams(locale),
        },
      })
      if (!response.data) {
        return []
      }
      return asSfccStores(response.data)
    } catch (e) {
      error.value = await salesforceErrorHandler(e)
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetches stores by location based on the provided longitude, latitude, postal code, and maximum distance.
   *
   * @param {Object} params - The parameters for fetching stores by location.
   * @param {number} [params.longitude] - The longitude of the location to search for stores. Required if `postalCode` is not provided.
   * @param {number} [params.latitude] - The latitude of the location to search for stores. Required if `postalCode` is not provided.
   * @param {string} [params.postalCode] - The postal code of the location to search for stores. Required if `longitude` and `latitude` are not provided.
   * @param {number} params.maxDistance - The maximum distance from the location to search for stores.
   * @param {number} [params.limit] - The maximum number of stores to return (SFCC defaults to 25, caps at 200).
   * @returns {Promise<SFCCStore[]>} A promise that resolves to an array of SFCCStore objects.
   * @throws Will throw an error if the request fails, handled by salesforceErrorHandler.
   *
   * @remarks
   * This function sets the `loading` state to `true` while fetching the data and sets it back to `false` once the operation is complete.
   * If an error occurs during the fetch operation, it is caught and assigned to the `error` state, and logged to the console.
   *
   * Note: Either `postalCode` or both `latitude` and `longitude` must be provided, otherwise the call will not succeed.
   */
  async function fetchStoresByLocation({
    longitude,
    latitude,
    postalCode,
    maxDistance,
    limit,
  }: {
    longitude?: number
    latitude?: number
    postalCode?: string
    maxDistance: number
    limit?: number
  }) {
    try {
      loading.value = true
      error.value = undefined
      const locationParams = postalCode ? { postalCode } : { longitude, latitude }
      const response = await useShopper().shopperStores.searchStores({
        headers: await useShopper().getAuthHeaders(),
        parameters: {
          ...locationParams,
          maxDistance,
          ...(limit && { limit }),
          countryCode,
          distanceUnit: 'km',
          siteId,
          ...sfccLocaleParams(locale),
        },
      })
      if (!response.data) {
        return []
      }
      return asSfccStores(response.data)
    } catch (e) {
      error.value = await salesforceErrorHandler(e)
      return []
    } finally {
      loading.value = false
    }
  }

  async function fetchAllStores() {
    try {
      loading.value = true
      const limit = 200 // Maximum number of stores per request
      const allStores = await fetchAllStoresWithOffset(0, limit)
      if (!allStores.data) {
        return []
      }
      if (allStores.total < limit) {
        return asSfccStores(allStores.data)
      }
      const promises = []
      for (let offset = limit; offset < allStores.total; offset += limit) {
        promises.push(fetchAllStoresWithOffset(offset, limit))
      }
      const results = await Promise.all(promises)
      return asSfccStores([...allStores.data, ...results.flatMap((result) => result.data)])
    } catch (e) {
      error.value = await salesforceErrorHandler(e)
      return []
    } finally {
      loading.value = false
    }
  }

  async function fetchAllStoresWithOffset(offset: number, limit: number) {
    return await useShopper().shopperStores.searchStores({
      headers: await useShopper().getAuthHeaders(),
      parameters: {
        countryCode,
        siteId,
        postalCode: mainPostcode,
        limit,
        offset,
        ...sfccLocaleParams(locale),
      },
    })
  }

  return {
    fetchStoresByIds,
    fetchStoresByLocation,
    fetchAllStores,
    loading: computed(() => loading.value),
    error: computed(() => error.value),
  }
}
